import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Search, X, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Activity, Clock, Hash } from 'lucide-react';
import baseapi from '../../../services/baseapi';

interface UserQueryStatisticsViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
}

export const UserQueryStatisticsView: React.FC<UserQueryStatisticsViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [userQueryStats, setUserQueryStats] = useState<any[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'username' | 'calls' | 'avg_time' | 'total_time'>('total_time');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = { cluster_id: activeCluster, clusted_id: activeCluster };
    if (clientId) params.client_id = clientId;

    try {
      const userRes = await baseapi.get('/db_monitor_dashboard/user_query_stats', { params });
      const uData = userRes.data?.data || (Array.isArray(userRes.data) ? userRes.data : []);
      setUserQueryStats(Array.isArray(uData) ? uData : []);
    } catch (err) {
      console.warn('UserQueryStatisticsView fetch error:', err);
      setUserQueryStats([]);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleSort = (field: 'username' | 'calls' | 'avg_time' | 'total_time') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }: { field: 'username' | 'calls' | 'avg_time' | 'total_time' }) => {
    if (sortBy !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-500 inline-block ml-1 opacity-60" />;
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-orange-400 inline-block ml-1" />
    ) : (
      <ArrowDown className="w-3 h-3 text-orange-400 inline-block ml-1" />
    );
  };

  const filteredAndSortedStats = useMemo(() => {
    const list = userQueryStats.filter((u) => {
      if (!userSearchQuery.trim()) return true;
      return u.username && u.username.toLowerCase().includes(userSearchQuery.toLowerCase().trim());
    });

    return list.sort((a, b) => {
      let aVal: any = 0;
      let bVal: any = 0;

      if (sortBy === 'username') {
        aVal = a.username || '';
        bVal = b.username || '';
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (sortBy === 'calls') {
        aVal = Number(a.calls || 0);
        bVal = Number(b.calls || 0);
      } else if (sortBy === 'avg_time') {
        const aCalls = Number(a.calls || 0);
        const aTotal = Number(a.total_time_ms || 0);
        aVal = aCalls > 0 ? aTotal / aCalls : 0;

        const bCalls = Number(b.calls || 0);
        const bTotal = Number(b.total_time_ms || 0);
        bVal = bCalls > 0 ? bTotal / bCalls : 0;
      } else if (sortBy === 'total_time') {
        aVal = Number(a.total_time_ms || 0);
        bVal = Number(b.total_time_ms || 0);
      }

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [userQueryStats, userSearchQuery, sortBy, sortOrder]);

  // KPI calculations
  const totalCalls = useMemo(() => {
    return userQueryStats.reduce((sum, u) => sum + (Number(u.calls) || 0), 0);
  }, [userQueryStats]);

  const totalTimeMs = useMemo(() => {
    return userQueryStats.reduce((sum, u) => sum + (Number(u.total_time_ms) || 0), 0);
  }, [userQueryStats]);

  const formatTotalTime = (ms: number) => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(2)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms.toFixed(0)}ms`;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <Users className="w-4 h-4" />
          </div>
          <h2 className="text-lg font-bold text-white">User Query Statistics</h2>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Total Database Users */}
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/90 border border-gray-700/60 rounded-xl p-3.5 shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-1">Total Users</div>
            <div className="text-xl font-bold font-mono text-white">{userQueryStats.length}</div>
          </div>
          <div className="p-2.5 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400">
            <Users className="w-5 h-5" />
          </div>
        </div>

        {/* Total Execution Calls */}
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/90 border border-gray-700/60 rounded-xl p-3.5 shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-1">Total Calls</div>
            <div className="text-xl font-bold font-mono text-emerald-400">{totalCalls.toLocaleString()}</div>
          </div>
          <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
            <Activity className="w-5 h-5" />
          </div>
        </div>

        {/* Total Execution Time */}
        <div className="bg-gradient-to-br from-gray-900/80 to-gray-900/90 border border-gray-700/60 rounded-xl p-3.5 shadow-md flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-1">Total Time</div>
            <div className="text-xl font-bold font-mono text-blue-400">{formatTotalTime(totalTimeMs)}</div>
          </div>
          <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400">
            <Clock className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-700/50 pb-2.5">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-orange-400" />
            <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider">
              User Execution Breakdown ({filteredAndSortedStats.length})
            </h3>
          </div>

          {/* Search by User */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search user..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="bg-gray-900/90 border border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-56 h-8"
            />
            {userSearchQuery && (
              <button
                onClick={() => setUserSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-[480px] border border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-700/80 bg-gray-900 shadow-sm text-[11px] text-gray-400 uppercase tracking-wider select-none">
                <th
                  onClick={() => handleSort('username')}
                  className="py-2.5 px-3.5 bg-gray-900 cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>User</span>
                    <SortIcon field="username" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('calls')}
                  className="py-2.5 px-3.5 text-right bg-gray-900 cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Calls</span>
                    <SortIcon field="calls" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('avg_time')}
                  className="py-2.5 px-3.5 text-right bg-gray-900 cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Avg / Call</span>
                    <SortIcon field="avg_time" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('total_time')}
                  className="py-2.5 px-3.5 text-right bg-gray-900 cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Total Time</span>
                    <SortIcon field="total_time" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200 bg-gray-900/40">
              {filteredAndSortedStats && filteredAndSortedStats.length > 0 ? (
                filteredAndSortedStats.map((u, idx) => {
                  const calls = Number(u.calls || 0);
                  const totalMs = Number(u.total_time_ms || 0);
                  const avgMs = calls > 0 ? totalMs / calls : 0;
                  const avgStr = avgMs > 1000 ? `${(avgMs / 1000).toFixed(2)}s` : `${avgMs.toFixed(2)}ms`;
                  const totalStr = totalMs > 1000 ? `${(totalMs / 1000).toFixed(2)}s` : `${totalMs.toFixed(0)}ms`;

                  return (
                    <tr key={idx} className="hover:bg-gray-800/60 transition-colors">
                      <td className="py-2.5 px-3.5 font-mono text-gray-200 font-medium">
                        {u.username || '-'}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-gray-300">
                        {calls.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-emerald-400 font-semibold">
                        {avgStr}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-mono text-blue-400 font-bold">
                        {totalStr}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-gray-400 italic">
                    {userSearchQuery
                      ? `No user query statistics matching "${userSearchQuery}".`
                      : 'No user query statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserQueryStatisticsView;
